import MarkdownIt from "markdown-it";
import Fuse from "fuse.js";
import Papa from "papaparse";

import lines_placeholder from "./assets/thumb_placeholder.png";
import { toast, UpdateOptions } from "react-toastify";
import {
    gfile,
    gcaption,
    IDocument,
    gextrprop,
    gjsonprop,
    gChatlogItem,
    livecachemap,
    gloggedinuser,
    gselectedgroup,
    gpagedimensions,
    livecachemapitem,
    gcampaignchatmemberrow,
    InteractionFeedbackItem,
    empty_extraction_field_list,
    chartTypeResult,
    invMissItemsKeys
} from "./interfaces";

import { scrollToPageNoSmoothCond } from "./scrll_glob";

import {
    invColOrderKey,
    assistantCodes,
    pdfSourceCodes,
    A4_ASPECT_RATIO,
    FLOOR_DATE_STRING,
    localStorageTaskIdsKey,
    columnTableSelectorSrc,
    localStorageRescaleWidthPrepend,
    localStorageInvItemTableColumnsKeyPrepend,
    localStorageRecoMatchesTableColumnsKeyPrepend,
    localStorageRecoMatchesTableColumnOrderKeyPrepend
} from "./lsts";
import { getConvoKeyTimestampHires } from "./components/SpeechRecognizer/process_v2";
import { saveItem, loadItem } from "./storage";
import { result } from "lodash";
import { setLocalFieldDefaults } from "./components/JsonGridInvoice/localFieldDefaults";

const md = new MarkdownIt();

const writeString = (view: DataView, offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
};

const mulberry32 = (a: number) => {
    return function () {
        let t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};


export const getDateToDisplay = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day} ${hours}:${minutes}m`;
};


export const getTranscriptFormattedDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} ${month} ${hours}h${minutes}m`;
};

export const getNewTranscriptItemKey = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
};

export const formatDateConvoList = (date: string) => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1); // Add 30 days
    const year = d.getFullYear();
    const month = `0${d.getMonth() + 1}`.slice(-2); // Pad month with zero
    const day = `0${d.getDate()}`.slice(-2); // Pad day with zero
    return `${year}-${month}-${day}`;
};

export const formatDate30DaysConvoList = (date: string) => {
    const d = new Date(date);
    d.setDate(d.getDate() - 30); // Add 30 days
    const year = d.getFullYear();
    const month = `0${d.getMonth() + 1}`.slice(-2); // Pad month with zero
    const day = `0${d.getDate()}`.slice(-2); // Pad day with zero
    return `${year}-${month}-${day}`;
};

export const onSTT = (text: string, language: string): void => {
    import.meta.env.DEV === true && console.log("‹whspr›", text, language);
};

export const createWavBuffer = (samples: Int16Array, sampleRate: number): ArrayBuffer => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    const bytesPerSample = 2; // 16-bit audio
    const numChannels = 1; // Mono
    const byteRate = sampleRate * numChannels * bytesPerSample;
    const blockAlign = numChannels * bytesPerSample;
    const bitsPerSample = 16;

    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + samples.length * bytesPerSample, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(view, 36, "data");
    view.setUint32(40, samples.length * bytesPerSample, true);
    for (let i = 0; i < samples.length; i++) {
        view.setInt16(44 + i * 2, samples[i], true);
    }
    return buffer;
};

export const prepWavBuffer = (audioChunks: Float32Array[], recognitionResultOffset: number) => {
    const audioCtx = new AudioContext();
    const sampleRate = audioCtx.sampleRate;
    const startSample = Math.floor((recognitionResultOffset / 10000000) * sampleRate);
    const comboAudio = new Float32Array(audioChunks.reduce((acc, chunk) => acc + chunk.length, 0));
    let offset = 0;
    for (const chunk of audioChunks) {
        comboAudio.set(chunk, offset);
        offset += chunk.length;
    }
    let relevantAudio = comboAudio.slice(startSample);
    const int16Array = new Int16Array(relevantAudio.length);
    for (let i = 0; i < relevantAudio.length; i++) {
        int16Array[i] = Math.max(-32768, Math.min(32767, Math.floor(relevantAudio[i] * 32768)));
    }
    let wavBuffer = createWavBuffer(int16Array, sampleRate);
    return wavBuffer;
};

export const handleFocus = (event: any) => {
    event.target.setSelectionRange(event.target.value.length, event.target.value.length);
};

export const encodeHTML = (str: string): string => {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, " ");
};

export const makeValidHtmlId = (str: string) => {
    // Remove any character that isn't a letter, number, hyphen, underscore, colon, or period
    let validStr = str.replace(/[^a-zA-Z0-9-_:.]/g, "");
    // Ensure starts with letter
    if (!/^[a-zA-Z]/.test(validStr)) validStr = "id-" + validStr;

    // Ensure it's not empty
    if (validStr.length === 0) validStr = "id";

    return validStr;
};

export const removeUnderscoreAndCapitalize = (str: string) => {
    return str
        .split("_")
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

export const shuffleArray = (array: string[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

export const getLoc = () => {
    // example result '/AA/000000AA_aaa0a000-0000-0000-0000-0aa00000aa00_AA0A0/chat'
    return window.location.hash.replace("#", "");
};

export const genNewConvoKey = (selectedAssistantType: string) => {
    if (selectedAssistantType == assistantCodes.cca) {
        return getConvoKeyTimestampHires().toString();
    } else {
        // convo key (datetime string)
        return new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 14);
    }
};

export const isElementVisible = (eid: string) => {
    const element = document.getElementById(eid);
    if (!element) return false;
    return true;
};

export const convertMarkdownHeadersToHTML = (markdownText: string) => {
    return markdownText
        .replace(/^####\s+(.*)$/gm, "<h4>$1</h4>")
        .replace(/^###\s+(.*)$/gm, "<h3>$1</h3>")
        .replace(/^##\s+(.*)$/gm, "<h2>$1</h2>")
        .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
};

export const convertMarkdownHeadersToHTMLWithNewLines = (markdownText: string) => {
    return markdownText
        ?.split("\n") // Split by double new lines to preserve paragraph structure
        .map(paragraph => {
            return paragraph
                .replace(/^####\s+(.*)$/gm, "<h4>$1</h4>")
                .replace(/^###\s+(.*)$/gm, "<h3>$1</h3>")
                .replace(/^##\s+(.*)$/gm, "<h2>$1</h2>")
                .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
        })
        .map(line => (line.match(/^<h[2-4]>/) ? line : `<p>${line}</p>`)) // Wrap non-header text in <p>
        .join("\n"); // Rejoin the modified lines
};

export const copyToClipboard = (text: string) => {
    navigator.clipboard
        .writeText(text)
        .then(() => {
            toast.success("Collected Data copied to Clipboard!");
        })
        .catch(err => {});
};

export const extractTextCptns = (content: string): gcaption | null => {
    content = content?.toString() || "";
    const colonIndex = content.indexOf(":");
    let contentText = "";
    if (colonIndex !== -1) {
        contentText = content.substring(colonIndex, content.length);
        content = content.substring(0, colonIndex);
    }
    const regex = /^(.*?)#page=([^_]+)_fid=(\d+)$/;
    const match = content.match(regex);
    if (match) {
        const [, filename, page, fileid] = match;
        return { content: contentText, filename, page, fileid };
    }
    return null;
};

export const addDotIfNeeded = (str: string) => {
    return str?.endsWith(".") ? str : str + ".";
};

export const adjustHeight = (questionInputContainer: any) => {
    const inputContainers = document.getElementsByClassName(questionInputContainer);
    for (let i = 0; i < inputContainers.length; i++) {
        const inputContainer = inputContainers[i] as HTMLElement;
        let textArr = inputContainers[i].querySelectorAll("textarea");
        for (let j = 0; j < textArr.length; j++) {
            const textElement = textArr[j] as HTMLTextAreaElement;
            if (parseInt(inputContainer.style.height) < inputContainer.scrollHeight) {
                let height = textElement.scrollHeight + 15;
                inputContainer.style.height = height + "px";
            } else {
                let height = textElement.scrollHeight + 15;
                inputContainer.style.height = "auto";
                inputContainer.style.height = height + "px";
            }
        }
    }
};

export const isTouchDevice = () => {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

export const contentPath = (filename: string, fileid: string) => {
    return `${filename}_fid=${fileid}`;
};

export const pdfCacheKey = (fId: string, modifiedDateString: string) => {
    try {
        let mdfd = new Date(modifiedDateString).getTime();
        return `${fId}_${mdfd}`;
    } catch {
        return `${fId}`;
    }
};

export const get_mod_str = (modified: string): string => {
    return new Date(modified).getTime().toString();
};

export const areAllFlagsTrue = (map: Map<number, { count: number; flag: boolean }>, numbers: number[]): boolean => {
    for (const number of numbers) {
        const item = map.get(number);
        if (!item || !item.flag) {
            return false;
        }
    }
    return true;
};

export const findRange = (numbers: number[], target: number): number[] => {
    numbers.sort((a: number, b: number) => a - b);
    let startIndex = numbers.findIndex((num: number) => num > target);
    let upper = startIndex === -1 ? numbers[numbers.length - 1] : numbers[startIndex];
    let endIndex = numbers.lastIndexOf(target);
    let lower = endIndex !== -1 ? numbers[endIndex] : numbers[startIndex - 1];
    if (lower === undefined || upper === undefined) {
        return [];
    }
    let result = [];
    for (let i = lower; i <= upper; i++) {
        result.push(i - 1);
    }
    if (target === upper) {
        result.push(upper - 1);
    }
    return result;
};

export const getDesiredHeight = (pageDims: gpagedimensions, srcLoc: string, groupId:string, widthSupplied?: number) => {
    const originalWidth = pageDims?.width;
    const originalHeight = pageDims?.height;

    let desiredWidth = 0
    if (widthSupplied){
        desiredWidth = widthSupplied;
    } else {
        desiredWidth = getDesiredWidth(srcLoc, groupId);
    }

    if (originalWidth && originalHeight) {
        const aspectRatio = originalHeight / originalWidth;
        return desiredWidth * aspectRatio;
    }
    return desiredWidth * A4_ASPECT_RATIO;
};

export const getDesiredWidth = (srcLoc: string, groupId:string, pullLocal?:boolean): number => {

    if(pullLocal && pullLocal == true){
        let localWidth = pullRescaleWidth(groupId, 0);
        if (localWidth && localWidth != 0) return localWidth;
    }

    let widthFactor = 1.9;
    if (srcLoc == pdfSourceCodes.modal || srcLoc == pdfSourceCodes.inv) {
        // directly related to components\XplrrPDF\XplrrPDF.module.css - pdfViewC
        let modifierReduct = 1;

        if (srcLoc === pdfSourceCodes.inv) modifierReduct = 1.8;

        if (window.innerWidth > 2400) {
            widthFactor = 2.6 * modifierReduct;
        } else if (window.innerWidth > 1800) {
            widthFactor = 1.8 * modifierReduct;
        } else if (window.innerWidth > 1600) {
            widthFactor = 1.6 * modifierReduct;
        } else if (window.innerWidth > 1400) {
            widthFactor = 1.4 * modifierReduct;
        } else if (window.innerWidth > 1200) {
            widthFactor = 1.3 * modifierReduct;
        } else if (window.innerWidth > 1000) {
            widthFactor = 1.2 * modifierReduct;
        } else if (window.innerWidth > 800) {
            widthFactor = 1.13 * modifierReduct;
        } else if (window.innerWidth > 600) {
            widthFactor = 1.08 * modifierReduct;
        } else if (window.innerWidth > 470) {
            widthFactor = 1.1 * modifierReduct;
        } else if (window.innerWidth > 350) {
            widthFactor = 1.11 * modifierReduct;
        } else if (window.innerWidth > 300) {
            widthFactor = 1.15 * modifierReduct;
        } else {
            // smallest
            widthFactor = 1 * modifierReduct;
        }
    } else {
        // sbs
        if (window.innerWidth > 800) {
            widthFactor = 1.85;
        } else {
            // smallest
            widthFactor = 1.85;
        }
    }
    try {
        return window.innerWidth / widthFactor;
    } catch (e) {
        return 800;
    }
};

export const getClassClientWidth = (className: string) => {
    const element = document.querySelector(`.${className}`) as HTMLElement;
    return element ? element.clientWidth : 0;
};

export const saveEntriesToGlobal = (currentEntries: number[]) => {
    window.globalCurrentVisibleEntries = currentEntries;
};

export const getDesiredWidthDiff = (srcLoc: string): number => {
    let widthFactor = 1.9;

    if (srcLoc === "modal" || srcLoc === "inv") {
        // Directly related to components\XplrrPDF\XplrrPDF.module.css - pdfViewC
        let modifierReduct = 2;

        if (window.innerWidth > 1800) {
            widthFactor = 1.8 * modifierReduct;
        } else if (window.innerWidth > 1600) {
            widthFactor = 1.6 * modifierReduct;
        } else if (window.innerWidth > 1400) {
            widthFactor = 1.4 * modifierReduct;
        } else if (window.innerWidth > 1200) {
            widthFactor = 1.3 * modifierReduct;
        } else if (window.innerWidth > 1000) {
            widthFactor = 1.2 * modifierReduct;
        } else if (window.innerWidth > 800) {
            widthFactor = 1.13 * modifierReduct;
        } else if (window.innerWidth > 600) {
            widthFactor = 1.08 * modifierReduct;
        } else if (window.innerWidth > 470) {
            widthFactor = 1.1 * modifierReduct;
        } else if (window.innerWidth > 350) {
            widthFactor = 1.11 * modifierReduct;
        } else if (window.innerWidth > 300) {
            widthFactor = 1.15 * modifierReduct;
        } else {
            //Smallest
            widthFactor = 1.15 * modifierReduct;
        }
    } else if (srcLoc == "diff") {
        // Directly related to components\XplrrPDF\XplrrPDF.module.css - pdfViewC
        let modifierReduct = 2;

        if (window.innerWidth > 1800) {
            widthFactor = 1.8 * modifierReduct;
        } else if (window.innerWidth > 1600) {
            widthFactor = 1.6 * modifierReduct;
        } else if (window.innerWidth > 1400) {
            widthFactor = 1.4 * modifierReduct;
        } else if (window.innerWidth > 1200) {
            widthFactor = 1.3 * modifierReduct;
        } else if (window.innerWidth > 1000) {
            widthFactor = 1.2 * modifierReduct;
        } else if (window.innerWidth > 800) {
            widthFactor = 1.13 * modifierReduct;
        } else if (window.innerWidth > 600) {
            widthFactor = 1.08 * modifierReduct;
        } else if (window.innerWidth > 470) {
            widthFactor = 1.1 * modifierReduct;
        } else if (window.innerWidth > 350) {
            widthFactor = 1.11 * modifierReduct;
        } else if (window.innerWidth > 300) {
            widthFactor = 1.15 * modifierReduct;
        } else {
            //Smallest
            widthFactor = 1.15 * modifierReduct;
        }
    } else {
        // "sbs"
        if (window.innerWidth > 800) {
            widthFactor = 1.85;
        } else {
            //Smallest
            widthFactor = 1.85;
        }
    }
    try {
        return window.innerWidth / widthFactor;
    } catch (e) {
        return 800;
    }
};

export const getDesiredHeightDiff = (pageDims: gpagedimensions, srcLoc: string) => {
    const A4_ASPECT_RATIO = 297 / 210;
    const originalWidth = pageDims?.width;
    const originalHeight = pageDims?.height;
    const desiredWidth = getDesiredWidthDiff(srcLoc);

    if (originalWidth && originalHeight) {
        const aspectRatio = originalHeight / originalWidth;
        return desiredWidth * aspectRatio;
    }
    return desiredWidth * A4_ASPECT_RATIO;
};

export const areAllFlagsTrueDiff = (numbers: number[], map?: Map<number, { count: number; flag: boolean }>): boolean => {
    for (const number of numbers) {
        const item = map?.get(number);
        if (!item || !item.flag) {
            return false;
        }
    }
    return true;
};

export const removeExtension = (filename: string) => {
    return filename.substring(0, filename.lastIndexOf(".")) || filename;
};

export const fetchThumbnailCached = async (fileid: string, filemod: any, fileowner: string, comp: string) => {
    let cacheKey = pdfCacheKey(fileid, filemod);

    // check if thumbnail exists in storage
    try {
        const cachedData = await loadItem(cacheKey);
        if (cachedData && typeof cachedData === "string" && cachedData.length > 0) {
            if (cachedData.startsWith("data:")) {
                // convert base64 back to blob and create blob URL
                const response = await fetch(cachedData);
                const blob = await response.blob();
                return URL.createObjectURL(blob);
            }
        }
    } catch (e) {
        console.warn("‹error_loading_cached_thumbnail›", e);
    }

    // if not cached or invalid, fetch from server
    const params = new URLSearchParams({
        of: fileowner,
        fid: fileid,
        cid: comp,
        mod: new Date(filemod).getTime().toString()
    });

    try {
        const response = await fetch(`/qthumb?${params.toString()}`);
        if (response.ok) {
            const blob = await response.blob();

            // convert blob to base64 for storage
            const reader = new FileReader();
            const base64Data = await new Promise<string>(resolve => {
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
            });

            // save base64 data to storage for future use
            try {
                await saveItem(cacheKey, base64Data);
            } catch (e) {
                console.warn("‹error_saving_thumbnail_cache›", e);
            }

            // return blob URL for immediate use
            return URL.createObjectURL(blob);
        }
    } catch (e) {
        console.error("‹errr_fetch_thumbnail_cached›", e);
    }

    return lines_placeholder;
};

export const isValidURL = (str: string) => {
    try {
        new URL(str);
        return true;
    } catch (_) {
        return false;
    }
};

export const getBaseURL = (url: string) => {
    try {
        const parsedURL = new URL(url);
        return `${parsedURL.protocol}//${parsedURL.hostname}`;
    } catch (error) {
        return null;
    }
};

export const getJsonType = (value: any): "string" | "number" | "boolean" | "array" | "object" => {
    if (Array.isArray(value)) return "array";

    switch (typeof value) {
        case "string":
            return "string";
        case "number":
            return "number";
        case "boolean":
            return "boolean";
        case "object":
            return "object";
        default:
            throw new Error("Unsupported type");
    }
};

export const sortObjectByKeyOrder = (obj: any, keyOrder: string[]) => {
    try {
        return Object.fromEntries(
            Object.entries(obj).sort(([a], [b]) => {
                return keyOrder.indexOf(a) - keyOrder.indexOf(b);
            })
        );
    } catch (e) {
        console.log("‹errr_sortobjectbykeyorder›", e);
        return obj || [];
    }
};

export const parseJsonToProperties = (json: any, description = ""): gjsonprop[] => {
    const parseObject = (obj: any, parentDesc: string): gjsonprop[] => {
        return Object.entries(obj).map(([key, value]) => {
            const type = getJsonType(value);

            let prop: gjsonprop = {
                name: key,
                description: `${parentDesc}${key}`,
                type
            };

            if (type == "string" || type == "number") prop["validation_source"] = "";

            if (type === "object" && value !== null) {
                prop.items = parseObject(value, `${parentDesc}${key}.`);
            } else if (type === "array" && (value as any).length > 0) {
                const firstElementType = getJsonType((value as any)[0]);
                if (firstElementType === "object") {
                    prop.items = parseObject((value as any)[0], `${parentDesc}${key}[].`);
                }
            }

            return prop;
        });
    };

    return parseObject(json, description);
};

export const safeJsonParse = (txt: string) => {
    try {
        return JSON.parse(txt);
    } catch {
        return "";
    }
};

export const keyToDateUtc = (itemKey: string) => {
    try {
        return new Date(
            Date.UTC(
                Number(String(itemKey).slice(0, 4)),       // year
                Number(String(itemKey).slice(4, 6)) - 1,   // month (0-indexed)
                Number(String(itemKey).slice(6, 8)),       // day
                Number(String(itemKey).slice(8, 10)),      // hour
                Number(String(itemKey).slice(10, 12)),     // minute
                Number(String(itemKey).slice(12, 14))      // second
            )
        )
    } catch {
        return new Date(Date.now() - 24 * 60 * 60 * 1000); // fallback to "yesterday" if parsing fails
    }
};

// Add a `stamp` Date to each campaign chat member row, derived from its `key` (YYYYMMDDHHmmss).
export const addStampToCampaignChatMemberRows = (rows: gcampaignchatmemberrow[]): gcampaignchatmemberrow[] => {
    return rows.map(row => ({
        ...row,
        stamp: row.key !== null && row.key !== undefined ? keyToDateUtc(String(row.key)) : null
    }));
};



export function markdownTableToHtmlOnly(markdown: string): string {
    const tableRegex = /\|(.+?)\|\s*\n\|(-+?\s*\|)+\s*\n((\|(.+?)\|\s*\n)*)/g;
    // Replace tables in the markdown string with HTML, leaving other parts untouched
    return markdown.replace(tableRegex, (match: string) => {
        return md.render(match); // Apply markdown-it rendering only to the table section
    });
}

export function genTbl(jsonData: string): string {
    let json = safeJsonParse(jsonData);
    return `<h3 style="list-style-type: none; padding-bottom: 6px;">Collected Information</h3><ul style="list-style-type: none; padding: 0;">
        ${Object.entries(json)
            .map(([key, value]) => `<li><strong>${key.replace(/_/g, " ")}:</strong> ${value}</li>`)
            .join("")}
    </ul>`;
}

export function jsonToKeyValueCsv(jsonData: Record<string, string>): string {
    return Object.entries(jsonData)
        .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`) // Format as "Key: Value"
        .join("\n"); // Join each pair into a new line
}

export function jsonToKeyValueCsv_v2(jsonData: Record<string, unknown>): string {
    return Object.entries(jsonData)
        .map(([key, value]) => {
            let stringValue: string;

            if (typeof value === "symbol") {
                // Option A: show the symbol
                stringValue = value.toString(); // e.g. "Symbol(react.element)"
                // Option B: or skip it entirely:
                // return null;  // and then filter(Boolean) later
            } else if (value == null) {
                stringValue = "";
            } else if (typeof value === "object") {
                // You can decide how you want objects shown
                try {
                    stringValue = JSON.stringify(value);
                } catch {
                    stringValue = "[object]";
                }
            } else {
                stringValue = String(value);
            }

            return `${key.replace(/_/g, " ")}: ${stringValue}`;
        })
        .filter(Boolean)            // in case you `return null` above
        .join("\n");
}

export function isEmptyObject(obj: any) {
    try {
        return Object.keys(obj).length === 0 && obj.constructor === Object;
    } catch {
        return false;
    }
}
export function notEmpty(obj: any, prop?: string) {
    try {
        if (prop) return obj && obj[prop] !== undefined && obj[prop] !== null;

        if (obj && obj !== undefined && obj !== null) {
            return true;
        } else {
            return false;
        }
    } catch {
        return false;
    }
}


export function getAlphaNumeric(str: string) {
    if (!str) return "";
    try {
        return str.replace(/[^А-Яа-яA-Za-z0-9]/g, "");
    } catch (e) {
        console.log("‹error_getAlphaNumeric›", str, e);
        return str;
    }
}

export function getLettersOnly(str: string) {
    return str.replace(/[^А-Яа-яA-Za-z0-9]/g, "");
}

export function getOnlyNumeric(str: string): string {
    return str.replace(/\D/g, "")?.toString(); // \D matches any non-digit character and replaces it with ""
}

export const removeEnclosingQuotes = (str: string) => {
    str = str.replace(/\\"/g, '"');
    return str.replace(/^["']|["']$/g, "");
};

export const newConvoStamp = () => {
    return Math.floor(Date.now());
};

export const newWiggleStamp = () => {
    return Math.floor(Date.now());
};

export const fuzzMtch = async (strings: string[], bigString: string, threshold = 0.4) => {
    console.log("⨀fuz⨀", bigString);
    if (!strings.length || !bigString) return null;
    const fuse = new Fuse(strings, {
        includeScore: true,
        threshold: threshold, // Lower = stricter matching
        findAllMatches: true
    });
    const results = fuse.search(bigString);

    if (results.length === 0) return null; // No match found

    // Ensure scores exist and find the best match
    const bestMatch = results.reduce((best, current) => {
        if (current.score === undefined) return best; // Skip undefined scores
        if (best.score === undefined || current.score < best.score) return current;
        return best;
    });

    return bestMatch?.item ? { match: bestMatch.item, score: bestMatch.score ?? 1 } : null;
};

// wildcard eval each one in strings against bigString
export function bestWildcardMatch(strings: string[], bigString: string) {
    console.log("⨀rev_match⨀", bigString);
    if (!strings.length || !bigString) return null;

    let bestMatch = null;
    let bestScore = 1; // Lower score = better match

    for (const str of strings) {
        const regex = new RegExp(str, "i"); // Case-insensitive search
        const match = bigString.match(regex);

        if (match) {
            const score = 1 - str.length / bigString.length; // Simple relevance scoring

            if (score < bestScore) {
                bestMatch = str;
                bestScore = score;
            }
        }
    }
    return bestMatch ? { match: bestMatch, score: bestScore } : null;
}

export const genRandomStr = (length: number) => {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
};

export const escapeSSML = (txt: string) => {
    return txt
        .replace(/&/g, "&amp;") // Encode ampersand first
        .replace(/</g, "&lt;") // Encode less-than
        .replace(/>/g, "&gt;") // Encode greater-than
        .replace(/"/g, "&quot;") // Encode standard double quotes
        .replace(/'/g, "&#39;") // Encode standard single quotes
        .replace(/`/g, "&#96;") // Encode backticks
        .replace(/„/g, "&#8222;") // Encode double low-9 quote
        .replace(/“/g, "&#8220;") // Encode left double quote
        .replace(/”/g, "&#8221;") // Encode right double quote
        .replace(/‚/g, "&#8218;") // Encode single low-9 quote
        .replace(/‘/g, "&#8216;") // Encode left single quote
        .replace(/’/g, "&#8217;"); // Encode right single quote
};

export const cleanValues = (prop: string): string => {
    return (prop?.replace(/\s+/g, "_") || "").toLowerCase(); // Replace spaces with underscores
};

export const safelySelectProp = (obj: any, prop: string, expected_fallback: any): any => {
    try {
        return obj[prop] !== undefined ? obj[prop] : null;
    } catch (e) {
        console.error("error selecting property:", e);
        return expected_fallback;
    }
};

export const getHeaderGlob = (loggedInUser: gloggedinuser) => {
    return {
        ...(loggedInUser?.token ? { Authorization: `Bearer ${loggedInUser.token}` } : {}),
        "X-MS-CLIENT-PRINCIPAL-NAME": loggedInUser?.mail ? loggedInUser.mail : "",
        "X-MS-CLIENT-PRINCIPAL-ID": loggedInUser?.userId ? loggedInUser.userId : "",
        "Content-Type": "application/json"
    };
};

export const additionalFixes = (str: string) => {
    return str.replace(/„|“/g, " "); // Replace smart quotes with a space
};

export const filterHighlightString = (str: string | undefined): string => {
    /* Remove any character that isn't a letter (including Cyrillic), number, or space, and convert to lowercase */
    return (str || "")
        .toLowerCase()
        .replace(/[^а-яa-z0-9]/gi, "")
        .trim();
};
export const filterHighlightString_V2 = (str: string | undefined): string => {
    /* Remove all except letters (including Cyrillic), numbers, and spaces, and convert to lowercase */
    return (str || "")
        .toLowerCase()
        .replace(/[^a-zа-яёѝ0-9\s]/giu, "")
        .trim();
};
export const wigglePixelV2 = async (target: number, original: number) => {
    await new Promise(r => setTimeout(r, 100));
    while (!window.globalCurrentVisibleEntries?.includes(target)) {
        window.global_wiggle_stamp = newWiggleStamp();
        await new Promise(r => setTimeout(r, 100));
        scrollToPageNoSmoothCond(target, original);
    }
    window.globalWigglePixel = 0;
};

export const sleep_abit = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export const stopWigglePixel = (src: string) => {
    window.globalWigglePixel = 0;
};

export const formatDuration = (seconds: number) => {
    const units = [
        { label: "yr", value: 31536000 }, // 365 days
        { label: "mo", value: 2592000 }, // 30 days
        { label: "wk", value: 604800 },
        { label: "d", value: 86400 },
        { label: "h", value: 3600 },
        { label: "min", value: 60 },
        { label: "sec", value: 1 }
    ];

    const parts = [];

    for (const unit of units) {
        const amount = Math.floor(seconds / unit.value);
        if (amount > 0) {
            parts.push(`${amount}${unit.label}`);
            seconds -= amount * unit.value;
        }
    }

    return parts.length ? parts.join(" ") : "0sec";
};

export const garble = (str: string, seed = 1337) => {
    const chars = [...str];
    let rng = mulberry32(seed);

    for (let i = chars.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join("");
};

export const getEncodedCurrentURL = () => {
    return encodeURIComponent(window.location.href);
};

export const prePreJsonInv = (group?: gselectedgroup) => {
    try {
        let jsonParsed = group?.extractionfieldlist || { excl: [], instr: [] };
        if (jsonParsed?.excl && jsonParsed?.excl.length > 0) {
            return jsonParsed?.excl;
        } else {
            return [];
        }
    } catch (e) {
        return [];
    }
};

export const init_inv_globs = () => {
    window.globalTextArray = [];
    window.globalTextArrayAudio = [];
    window.globalTextMap = new Map<string, boolean>();
    window.globalPDFExtr = [];
    window.globalPDFPageMap = new Map<number, any>();
    window.globalPDFContent = [];
    window.globalAllNessesaryPDFPagesLoaded = false;
};

export const prepHtml = (analysisContent: any): string | TrustedHTML => {
    return convertMarkdownHeadersToHTMLWithNewLines(markdownTableToHtmlOnly(convertMarkdownHeadersToHTML(analysisContent?.result || "")))
        ?.replace(new RegExp("```html|```", "g"), "")
        .replace(/&lt;b&gt;/g, "<b>")
        .replace(/&lt;\/b&gt;/g, "</b>")
        .replace("\n", "<br>");
};

export const get_grid_data = (invoicedata: any): any => {
    return invoicedata ? (typeof invoicedata == "string" ? JSON.parse(invoicedata) : invoicedata) : empty_extraction_field_list;
};

export const setValueAtPath = (obj: any, path: (string | number)[], value: any): any => {
    if (path.length === 0) return value;

    const [firstKey, ...restPath] = path;

    let updatedValue;
    if (restPath.length === 0) {
        updatedValue = value;
    } else {
        const currentObj = obj && obj[firstKey] !== undefined ? obj[firstKey] : typeof restPath[0] === "number" ? [] : {};
        updatedValue = setValueAtPath(currentObj, restPath, value);
    }

    if (Array.isArray(obj)) {
        const newArray = [...obj];
        newArray[firstKey as number] = updatedValue;
        return newArray;
    } else {
        return { ...obj, [firstKey]: updatedValue };
    }
};

export const getEmptyHeaders = (loggedInUser?: gloggedinuser): Record<string, string> => {
    if (!loggedInUser) {
        return {
            "Content-Type": "application/json"
        };
    } else {
        return {
            "X-MS-CLIENT-PRINCIPAL-NAME": loggedInUser?.mail ? loggedInUser.mail : "",
            "X-MS-CLIENT-PRINCIPAL-ID": loggedInUser?.userId ? loggedInUser.userId : "",
            "Content-Type": "application/json"
        };
    }
};

export const fetchTaskStatus = async (taskId: string) => {
    try {
        const statusResponse = await fetch(`/task-status/${taskId}`);
        if (!statusResponse.ok) {
            import.meta.env.DEV === true && console.log("‹errr_fetch_task_01›", statusResponse);
            return {};
        }

        return await statusResponse.json();
    } catch (error) {
        import.meta.env.DEV === true && console.log("‹errr_fetch_task_02›", error);
        return {};
    }
};

export const removeTaskIdFromLocal = (taskId: string) => {
    let taskIds = localStorage.getItem(localStorageTaskIdsKey)?.split(";");
    taskIds = taskIds?.filter(id => id !== taskId);
    localStorage.setItem(localStorageTaskIdsKey, taskIds ? taskIds.join(";") : "");
};

export const itterPositionOk = (itterCounter: number, upperLim: number) => {
    // first 10 always, then every 5th until upperLim
    return (itterCounter % 10 === 0 || itterCounter % 10 === 5 || itterCounter < upperLim) && itterCounter < upperLim;
};

export const inMsgConstruct = (chkResp: any) => {
    return `Processing Files... ${chkResp.progresscount} of ${chkResp.filecount} Done`;
};

export const getDynamicEnd = (isvideofiles: any) => {
    return isvideofiles ? "/upload_video_fw" : "/upload";
};

export const logRespErr = (resp: any) => {
    console.log(`‹errr_content_ingest› ${resp.statusText}`);
};

export const toastFinOpt = (msg: string): UpdateOptions => {
    return {
        render: msg,
        autoClose: 7000,
        isLoading: false
    };
};

export const toastFinErr = (msg: string): UpdateOptions => {
    return {
        render: `${msg}`,
        type: "error",
        autoClose: 10000,
        isLoading: false
    }
}

export const getValueAtPath = (obj: any, path: (string | number)[]) => {
    return path.reduce((acc, key) => {
        if (acc === undefined || acc === null) {
            return undefined;
        } else {
            return acc[key];
        }
    }, obj);
};

export const deleteValueAtPath = (obj: any, path: (string | number)[]): any => {
    if (path.length === 0) return obj;

    const [firstKey, ...restPath] = path;
    if (restPath.length === 0) {
        if (Array.isArray(obj)) {
            const newArray = [...obj];
            newArray.splice(firstKey as number, 1);
            return newArray;
        } else {
            const { [firstKey]: deletedKey, ...rest } = obj;
            return rest;
        }
    } else {
        if (Array.isArray(obj)) {
            const newArray = [...obj];
            newArray[firstKey as number] = deleteValueAtPath(obj[firstKey as number], restPath);
            return newArray;
        } else {
            return { ...obj, [firstKey]: deleteValueAtPath(obj[firstKey], restPath) };
        }
    }
};

export const toggleItems = (arrayItemPathAsString: string, fieldsArray: string[]) => {
    fieldsArray.forEach(field => {
        const element = document.getElementById(`${arrayItemPathAsString}-${field}`);
        if (element) element.style.display = element.style.display === "none" ? "" : "none";
    });
};

export const pathToBreadcrumb = (path: (string | number)[]): string => path.map(p => String(p)).join(">");

export const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const replaceAndParseJson = (grpsresp: any, i: number) => {
    return JSON.parse(
        grpsresp[i]?.docextractionconfig
            ?.replace(/^"(.*)"$/, "$1")
            .replace(/\\/g, "")
            .replace(/"null"/g, '""') ||
            {} ||
            {}
    );
};

export const replaceAndParseJsonV2 = (grpsresp: any, i: number) => {
    let v = grpsresp[i]?.docextractionconfig;
    try {
        while (typeof v === "string") v = JSON.parse(v);
    } catch {
        return [];
    }
    return Array.isArray(v) ? v : [];
};


// Parses JSON that may be stored as a string, or a string inside a string (double-encoded)
export const parseJsonField = <T,>(val: unknown, fallback: T): T => {
    let v = val;
    try {
        while (typeof v === "string") v = JSON.parse(v);
    } catch {
        return fallback;
    }
    return (v ?? fallback) as T;
};

export const replaceExtrConfig = (grpsresp: any, i: number) => {
    return grpsresp[i]?.docextractionconfig?.replace(/^"(.*)"$/, "$1").replace(/\\/g, "").replace(/"null"/g, '""') || {} || {}
};


export const getPagesDims = (cacheKey:string, fl_dims:gpagedimensions[], glob_dims:gpagedimensions[] | undefined) => {
    let pgDms = window.globalPDFCache.get(cacheKey)?.pageDimensions;
    if (!pgDms || pgDms.length == 0) pgDms = fl_dims;
    return pgDms;
};

export const setGGlobalDims = (dims:gpagedimensions[], src_loc:string, groupId:string) => {
    if(dims && dims.length > 0) {
        window.globalPDFPageDimensions = dims.map((dim, index) => ({ ...dim, scaledHeight: getDesiredHeight(dim, src_loc, groupId) }));
        import.meta.env.DEV === true && console.log("‹globdimsres›", window.globalPDFPageDimensions);
    }  else {
        console.log("‹errr_globdimsres_empty_arr");
    }

};

export const getNewWidthActual = (innerWidthNum: number, eclientxNum: number, leftResizingNum: number) => {
    return Math.min(
        Math.max(
            innerWidthNum * 0.25,
            Math.min(innerWidthNum / 2, eclientxNum - leftResizingNum)
        ),
        innerWidthNum / 2
    ) ;
};

export const pullRescaleWidth = (group_id: string, defaultWidth: number) => {
    const rescaleWidth = localStorage.getItem(`${localStorageRescaleWidthPrepend}${group_id}`);
    if (!rescaleWidth) return defaultWidth;

    return parseFloat(rescaleWidth);
};

export const storeRescaleWidth = (group_id: string, width: number) => {
    localStorage.setItem(`${localStorageRescaleWidthPrepend}${group_id}`, width.toString());
};

export const getLastSegmentLog = (path: string) => {
    return path.split("\\").pop() || "aud.wav";
};

export const getSingleFeedback = (item: any) => {
    let interactionFeedback: InteractionFeedbackItem[] = [];
    if (item.positive == 1 || item.positive == 0) {
        interactionFeedback = [
            {
                feedback: item.feedback ? item.feedback : "",
                positive: item.positive == 1 ? "Positive" : item.positive == 0 ? "Negative" : "No Feedback",
                numericFeedback: item.positive == 1 ? 1 : item.positive == 0 ? -1 : 0,
                numericCount: item.positive == 1 ? 1 : item.positive == 0 ? 0 : 0
            }
        ];
    }
    return interactionFeedback;
}

export const downloadCSV = (fltrdChats:gChatlogItem[]) => {
    const csv = Papa.unparse(
        fltrdChats
            .map(item => ({
                ...item,
                key: `${item.key.toString().slice(0, 4)}-${item.key.toString().slice(4, 6)}-${item.key.toString().slice(6, 8)} ${item.key
                    .toString()
                    .slice(8, 10)}:${item.key.toString().slice(10, 12)}:${item.key.toString().slice(12, 14)}`
            }))
            .map(item => ({
                ...item,
                collecteddata: JSON.stringify(item.collecteddata || {})
            })),
        {
            quotes: true, // wrap all fields in quotes for safety
            header: true  // include header row automatically
        }
    );
    // add utf-8 bom for correct cyrillic encoding
    const csvWithBom = "\uFEFF" + csv;
    const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kgnf_analytics_chatlog.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
};

export const reverseDiscount = (finalPrice:number, discountValue:number, isPerc = true) => {
    if (isPerc) {
        console.log("‹rev_disc_perc›", finalPrice, discountValue);
        if (discountValue >= 100) throw new Error("Discount percent must be less than 100.");

        const factor = 1 + (discountValue / 100);
        return Math.round((finalPrice / factor) * 100) / 100;

    } else {
        console.log("‹rev_disc_fixed›", finalPrice, discountValue);
        // fixed value mode
        return Math.round((finalPrice + discountValue) * 100) / 100;
    }
}


export const calcDiscountedTotal = (finalPrice:number, discountValue:number, isPerc = false) => {
    if (isPerc == true) {
        import.meta.env.DEV && console.log("‹is_perc›")
        const discount = finalPrice * (discountValue / 100);
        let result = finalPrice - discount
        return Math.round(result * 100) / 100;;

    } else {
        import.meta.env.DEV && console.log("‹not_perc›")
        let result = finalPrice - discountValue
        return Math.round(result * 100) / 100;
    }
}

 export const stillInGroup = (groupId: string | undefined) => {
    if (!groupId) return true;
    return ((decodeURIComponent(window.location.href)).indexOf(groupId) >= 0)
}

// clean properties function
export const cleanTrProps = (properties: gextrprop[]): gextrprop[] => {
    if (!Array.isArray(properties)) return [];
    return properties
        .filter(prop => prop.name !== "")
        .map(prop => {
            let cleaned: gextrprop = {
                name: prop.name.replace(/\s+/g, "_"), // replace spaces with underscores
                description: prop.description,
                type: prop.type
            };
            if (prop.type == "string" || prop.type == "number") cleaned.validation_source = prop.validation_source;

            // if it's an array type and has items, clean them recursively
            if (prop.type === "array" && prop.items && prop.items.length > 0) {
                cleaned.items = cleanTrProps(prop.items);
            } else if (prop.type === "object" && prop.items && prop.items.length > 0) cleaned.items = cleanTrProps(prop.items);

            return cleaned;
        })
        .filter(prop => {
            // remove properties that have empty items array when type is 'array'
            if (prop.type === "array" || prop.type === "object") return prop.items && prop.items.length > 0;

            return true;
        });
};

export function getResponsiveValue({
        height = window.innerHeight,
        minHeight = 400,
        maxHeight = 3000,
        minValue = 0.1,
        maxValue = 0.5,
    } = {}) {
    // Clamp height within bounds
    const h = Math.min(Math.max(height, minHeight), maxHeight);

    // Linear interpolation (direct: taller screen → larger value)
    const ratio = (h - minHeight) / (maxHeight - minHeight);
    const value = minValue + (maxValue - minValue) * ratio;

    return Number(value.toFixed(3));
}

export function getNestVal(obj: any, path: string, defaultValue: any = undefined) {
    return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? Array.isArray(acc[key] ?? {}) == true ? acc[key][0] : acc[key] : undefined), obj) ?? defaultValue;
}

export const formatSQL = (sql: string) => {
    const keywords = ["SELECT", "FROM", "WHERE", "JOIN", "LEFT JOIN", "RIGHT JOIN", "INNER JOIN", "GROUP BY", "HAVING", "ORDER BY"];
    let formattedQuery = sql.trim();

    keywords.forEach(keyword => {
        formattedQuery = formattedQuery.replace(new RegExp(keyword, "gi"), "\n" + keyword);
    });

    return formattedQuery
};

// cca replacements
export const ccaReplacements = (txt: string, assistantType: string) => {
    if (assistantType == assistantCodes.cca) {
        txt = txt.replace("салва ", "св ").replace("сава ", "св ");
        txt = txt.replace("силва ", "св ").replace("сава ", "св ");
        const regex = /са в (?=\d\s?\d?\s?\d)/g;
        txt = txt.replace(regex, "св ");
    }
    return txt;
};

// get date -5 years
export const getDateFiveYearsAgo = (): Date => {
    const now = new Date();
    const fiveYearsAgo = new Date(now);
    fiveYearsAgo.setFullYear(now.getFullYear() - 5);
    return fiveYearsAgo;
}


const getTableSelectorLocKey = (srcTable: columnTableSelectorSrc, groupId: string) => {
    let localStorageDynamicKey = `${localStorageInvItemTableColumnsKeyPrepend}${groupId}`
    if (srcTable == "reco_matches" as columnTableSelectorSrc) localStorageDynamicKey = `${localStorageRecoMatchesTableColumnsKeyPrepend}${groupId}`
    return localStorageDynamicKey;
}

// set to local stor - invoice item table visible columns
export const setLocInvItemCols = (groupId: string, newHidden: Set<string>, srcTable: columnTableSelectorSrc) => {
    let localStorageDynamicKey = getTableSelectorLocKey(srcTable, groupId);
    localStorage.setItem(localStorageDynamicKey, JSON.stringify(Array.from(newHidden || [])));
};

// get from local stor - invoice item table visible columns
export const getLocInvItemCols = (groupId: string, srcTable: columnTableSelectorSrc ) => {

    setLocalFieldDefaults();


    let localStorageDynamicKey = getTableSelectorLocKey(srcTable, groupId);
    let invItemColsStr = localStorage.getItem(localStorageDynamicKey);

    if (invItemColsStr) {
        try {
            return JSON.parse(invItemColsStr);
        } catch (e) {
            localStorage.removeItem(localStorageDynamicKey);
            return ['not_set'];
        }
    } else {
        return ['other_01',
            'other_02',
            'other_03',
            'other_04',
            'reconciliation.company_code',
            'reconciliation.confirmed_qty',
            'reconciliation.contract_ref_number',
            'reconciliation.contractor_address',
            'reconciliation.contractor_vat_identification',
            'reconciliation.customer_address',
            'reconciliation.customer_name',
            'reconciliation.customer_vat_identification',
            'reconciliation.discount',
            'reconciliation.discount_is_perc',
            'reconciliation.doc_category',
            'reconciliation.doc_total_currency',
            'reconciliation.doc_type',
            'reconciliation.exchange_rate',
            'reconciliation.gross_weight',
            'reconciliation.iban',
            'reconciliation.id',
            'reconciliation.idoc_created_date',
            'reconciliation.idoc_created_time',
            'reconciliation.idoc_type',
            'reconciliation.incoterms_code',
            'reconciliation.incoterms_location',
            'reconciliation.invoice_currency',
            'reconciliation.invoice_date',
            'reconciliation.invoice_id',
            'reconciliation.invoice_total',
            'reconciliation.item_barcode',
            'reconciliation.item_client_code',
            'reconciliation.item_description',
            'reconciliation.item_mapping_number',
            'reconciliation.item_measure',
            'reconciliation.item_number',
            'reconciliation.item_quantity',
            'reconciliation.item_seller_code',
            'reconciliation.item_total_price',
            'reconciliation.item_type',
            'reconciliation.item_unit_price',
            'reconciliation.legacy_po_prop',
            'reconciliation.line_action',
            'reconciliation.line_ref_doc_line',
            'reconciliation.line_ref_doc_number',
            'reconciliation.line_ref_doc_type',
            'reconciliation.local_currency',
            'reconciliation.material_group',
            'reconciliation.message_type',
            'reconciliation.net_weight',
            'reconciliation.ordering_party_name',
            'reconciliation.ordering_party_number',
            'reconciliation.payment_due_date',
            'reconciliation.payment_terms_code',
            'reconciliation.payment_terms_days',
            'reconciliation.price_denominator',
            'reconciliation.price_qty_unit',
            'reconciliation.purch_group',
            'reconciliation.purch_org',
            'reconciliation.purchase_order',
            'reconciliation.quantity_alt',
            'reconciliation.receiver_port',
            'reconciliation.recipient_no',
            'reconciliation.release_date',
            'reconciliation.requested_delivery_date',
            'reconciliation.requested_qty',
            'reconciliation.schedule_seq',
            'reconciliation.sender_port',
            'reconciliation.storage_location',
            'reconciliation.vat',
            'reconciliation.ai_recon.purchase_order_item_number',
            'reconciliation.vendor_number',
            'reconciliation.weight_unit',
            'reconciliation.ai_recon.invoice_item_number']; // default hidden columns
    }
};

// set to local stor - reco matches table column order (keyed by prepend + groupId/selectionId)
export const setLocRecoColOrder = (groupId: string, order: string[]) => {
    localStorage.setItem(`${localStorageRecoMatchesTableColumnOrderKeyPrepend}${groupId}`, JSON.stringify(order || []));
};

// get from local stor - reco matches table column order (returns [] when unset/corrupt)
export const getLocRecoColOrder = (groupId: string): string[] => {
    let key = `${localStorageRecoMatchesTableColumnOrderKeyPrepend}${groupId}`;
    let stored = localStorage.getItem(key);
    if (stored) {
        try {
            let parsed = JSON.parse(stored);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            localStorage.removeItem(key);
            return [];
        }
    }
    return [];
};

export const noZoneDateStrToDate = (dateString: string): Date => {
    try {
        return new Date(dateString.replace(' ', 'T') + 'Z');
    } catch (e) {
        console.log("‹errr_str_to_dt›");
        return (new Date(FLOOR_DATE_STRING));
    }
}
export const strToDate = (dateString: string): Date => {
    try {
        return new Date(dateString);
    } catch (e) {
        console.log("‹errr_str_to_dt›");
        return (new Date(FLOOR_DATE_STRING));
    }
}

export const strToInt = (str: any): number => {
    try {
        return parseInt(str);
    } catch (e) {
        return 0;
    }
}

// legacy
export const cacheComp = (oldArr: livecachemapitem[], newArr: livecachemapitem[]) => {
    const changed: livecachemapitem[] = [];
    const map = new Map(oldArr.map(x => [x.fileid, x]));
    for (const next of newArr) {
        const prev = map.get(next.fileid);
        if (!prev) {
            changed.push(next);       // new item
            continue;
        }
        if (
            prev.docstatus !== next.docstatus ||
            prev.modified.getTime() !== next.modified.getTime()
        ) {
            changed.push(next);       // updated
        }
    }
    return changed;
}

export const cacheCompV2 = (oldMap: livecachemap,newMap: livecachemap) => {
    const forRefresh: livecachemap = new Map();
    const forRemoval: livecachemap = new Map();

    // 1) refresh → new or changed
    for (const next of newMap.values()) {
        const prev = oldMap.get(next.fileid);
        if (!prev) {
            forRefresh.set(next.fileid, next); // brand new
            continue;
        }
        if (prev.docstatus !== next.docstatus || prev.modified.getTime() !== next.modified.getTime()) forRefresh.set(next.fileid, next); // updated
    }
    // 2) remove → existed before but missing now
    for (const old of oldMap.values()) {
        if (!newMap.has(old.fileid)) forRemoval.set(old.fileid, old);
    }
    return { forRefresh, forRemoval };
};

export const upsertGFiles = (oldArr: gfile[], newArr: gfile[]): gfile[] => {
    // create index for quick lookup of existing items by fileid
    const index = new Map<string, number>();

    // build lookup for existing items
    for (let i = 0; i < oldArr.length; i++) {
      index.set(oldArr[i].fileid, i);
    }

    const result = [...oldArr]; // immutability preserved

    // insert or replace
    for (const item of newArr) {
      const pos = index.get(item.fileid);

      if (pos !== undefined) {
        result[pos] = item;       // replace existing
      } else {
        result.push(item);        // insert new
      }
    }

    // sort
    result.sort((a, b) => Number(a.fileid) - Number(b.fileid));
    return result;
}

export const fileHotLinkInURL = (): [boolean, string] => {
    let subpaths = window.location?.hash?.replace("#", "").split("/").filter(Boolean);
    const isHotLink: boolean = (subpaths?.length ?? 0) > 3 && subpaths?.[2] === "library" && !!subpaths?.[3] && strToInt(subpaths?.[3] ?? "0") > 0;
    let pathThree = subpaths?.[3] || "0";
    return [isHotLink, pathThree];
}

export const getByPath = (obj: any, path: string) =>
    path
      .split(".")
      .reduce((acc: any, key: string) => acc?.[key], obj);

export const getLocalSortState = (lockey: string, oid: string | number, fallback: any = null) => {
    const savedSortState = localStorage.getItem(`${lockey}_${oid}`);
    if (savedSortState) {
        try {
            return JSON.parse(savedSortState);
        } catch (e) {
            return fallback
        }
    } else {
        return fallback
    }
}

export const setLocalSortState = (lockey: string, oid: string | number, data: any = null) => {
    localStorage.setItem(`${lockey}_${oid}`, JSON.stringify(data));
}

export const getLocInvColumnOrder = (groupId: string): string[] => {
    try {
        import.meta.env.DEV && console.log("Getting local invoice column order for groupId:", `${invColOrderKey}${groupId}`);
        const stored = localStorage.getItem(`${invColOrderKey}${groupId}`);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        import.meta.env.DEV && console.error("Error loading column order from localStorage:", error);
        return [];
    }
};

export const setLocInvColumnOrder = (groupId: string, order: string[]) => {
    try {
        localStorage.setItem(`${invColOrderKey}${groupId}`, JSON.stringify(order));
    } catch (error) {
        console.error("Error saving column order to localStorage:", error);
    }
};

export const toFixedSafe = (num: any, decimalPlaces: number): string => {
    try {
        const parsedNum = parseFloat(num);
        if (isNaN(parsedNum)) return "0";
        return parsedNum.toFixed(decimalPlaces);
    } catch (e) {
        return "0";
    }
};

export const roundSafe = (num: any, decimalPlaces: number): number => {
    try {
        const parsedNum = parseFloat(num);
        if (isNaN(parsedNum)) return 0;
        const factor = Math.pow(10, decimalPlaces);
        return Math.round(parsedNum * factor) / factor;
    } catch (e) {
        return 0;
    }
};

export const getPropFromArr = (arr: any[], prop: string, fall: any) => {
    if (!Array.isArray(arr)) return fall;
    return arr.map(item => item?.[prop] ?? fall);
};

export const getProp = (obj: any, prop: any, fall: any) => {
    /** Get object prop or fallback
     * @param obj - Source Obj
     * @param prop - Prop Name
     * @param fall - Fallback Value
     * @returns Value of Prop or Fallback
    */
    return obj?.[prop] ?? fall;
}

export const parseLangKeys = (v?: string): string[] => {
    if (!v) return [];
    const s = v.trim();
    if (!s || s === "auto") return ["auto"];
    return s
        .split(";")
        .map(x => x.trim())
        .filter(Boolean);
}

export const serializeLangKeys = (keys: string[]): string => {
    // store "auto" when nothing selected
    return keys.length ? keys.join(";") : "auto";
}

export const hasHtmlTagsStrict = (input: unknown): boolean => {
    if (typeof input !== "string") return false;

    // Requires a closing '>' and a valid tag-ish structure; ignores "< 3" etc.
    const STRICT_TAG_RE =
        /<(?:\/\s*)?[A-Za-z][A-Za-z0-9-]*(?:\s+[A-Za-z_:][A-Za-z0-9:._-]*(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*\s*(?:\/\s*)?>/;

    return STRICT_TAG_RE.test(input);
};

export const notEmptyStr = (input: unknown): boolean => {
    return typeof input === "string" && input.trim() !== "";
};
export const isItChartArray = (context: any): chartTypeResult => {
    if (context.chartjs_json){
        let parsed = []
        if (typeof context.chartjs_json === "string") parsed = safeJsonParse(context.chartjs_json)

        if (Array.isArray(parsed)){
            if (parsed.length > 0) {
                return "isArrayFull";
            } else {
                return "isArrayEmpty";
            }
        } else {
            return "isElse";
        }
    }
    return "isNull";
}


export const trimLow = (text: string): string => {
    try {
        return text?.trim().toLowerCase() || '';
    } catch (e) {
        return '';
    }
}

export const prepUcpData = (gridData: any) => {
    const displayData = (gridData?.data || gridData)?.data ? (gridData?.data || gridData) : gridData?.data || gridData;
    console.log("‹prepUcpData›", { gridData, displayData });
    if (Array.isArray(displayData)) {
        if (displayData.length == 1) {
            const result = displayData[0];
            // delete result.page;
            if (result?.data) return result.data;

            return result;
        }
        return displayData;
    } else {
        return displayData;
    }
}

// inverse of prepUcpData: put edited display data back into the original wrapper structure
export const rewrapUcpData = (original: any, updated: any) => {
    const hasOuter = !!original?.data;
    const inner = original?.data || original;
    let newInner = updated;
    if (Array.isArray(inner) && inner.length == 1) {
        const first = inner[0];
        newInner = [first?.data ? { ...first, data: updated } : updated];
    }
    return hasOuter ? { ...original, data: newInner } : newInner;
};

export const getContentPathWithId = (itemSelectionString: string, itemKey: string) => {
    const itemSel = itemSelectionString.split(';')[0];
    const contPath = `${itemSel}_fid=${itemKey}`;
    return contPath;
};


export const getMissItemsArrayNoDiscounts = (item: IDocument, propName: invMissItemsKeys) => {
    return Array.isArray(item?.invoicedataobj?.[propName]) ? item?.invoicedataobj?.[propName]?.filter((itm: any) => itm?.item_type != "Discount/Charge") : [];
}



export const isFalseOrLengthZero = ( item: IDocument, propName: invMissItemsKeys ) => {
    return item?.invoicedataobj?.[propName] == 0 || (getMissItemsArrayNoDiscounts(item, propName).length == 0);
}

export const getValidationResult = (item: any) => {
    return (
        item?.invoicedataobj?.['grand_total_mismatch'] == false &&
        item?.invoicedataobj?.['insufficient_totals_data'] == false &&
        isFalseOrLengthZero(item, 'items_with_insufficient_data') &&
        isFalseOrLengthZero(item, 'items_with_mismatches')
    );
}


export const not_empty_prop = (input: any, prop: string): boolean => {

    if (input && input?.[prop]){
        if (typeof input?.[prop] === "string" && input[prop].trim() == "") {
            return false;
        } else if (Array.isArray(input?.[prop]) && input[prop].length < 1) {
            return false;
        } else if (typeof input?.[prop] === "object" && Object.keys(input[prop]).length < 1) {
            return false;
        } else if (typeof input?.[prop] === "number" && isNaN(input[prop])) {
            return false;
        } else if (typeof input?.[prop] === "boolean" && input?.[prop] == false) {
            return false;
        } else if (input?.[prop] === null) {
            return false;
        } else {
            return true;
        }
    } else {
        return false;
    }
};


export const isArrNonempty = (input: any, prop: string): boolean => {
    try {
        if (input && input?.[prop]){
            if (Array.isArray(input?.[prop]) && input[prop].length > 0) {
                return true;
            } else {
                if (typeof input?.[prop] === "string" && input[prop].trim() !== "") {
                    return true;
                } else {
                    return false;
                }
            }
        } else {
            return false;
        }
    } catch (e) {
        return false;
    }
}

export const getVSourceFid = (fileList:any[]) => {
    return fileList?.map(file => ({ key: file.fileid.toString(), text: file.name })) || [];
};

export const checkVSSourceIDExists = (fileList:any[], fid:string) => {
    return fileList?.some(file => file.fileid.toString() === fid) || false;
}

export const get_all_keys = (invoice_items: any[], prop: string): string[] => {
    const reconciliations = invoice_items?.map((item: any) => item[prop] ?? {}) ?? [];

    const allKeys = [
        ...new Set(reconciliations.flatMap(Object.keys))
    ];
    return allKeys;
};

// keep original array sorted the same as current array by aligning them by item_number
export const reorderByItemNumber = (sourceItems:any[], targetItems:any[]) => {
    const orderMap = new Map(sourceItems.map((item, index) => [item.item_number, index]));
    return [...targetItems].sort((a, b) => (orderMap.get(a.item_number) ?? Infinity) - (orderMap.get(b.item_number) ?? Infinity));
};

// reorder boolean selection array by item_number alignment
export const reorderSelectionByItemNumber = (sortedItems: any[], prevItems: any[], prevSelected: boolean[]): boolean[] => {
    const selMap = new Map(prevItems.map((it, i) => [it.item_number, !!prevSelected[i]]));
    return sortedItems.map(it => !!selMap.get(it.item_number));
};